import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { customAlphabet } from 'nanoid';
import { store } from '../../store/store';
import {
  PROVISION_API_ENDPOINT,
  CIPHER_ACCOUNT_ID,
} from '../../utils/constants/apiConstants';
import { VITE_WALLET_IS_TEST_ENVIRONMENT } from '../../utils/constants/envConstants';
import { SESSION_ID_SESSION_STORAGE_KEY } from '../../utils/constants/sessionStorageConstants';
import {
  GOOGLE_INTEGRATOR_ID,
  GOOGLE_HL,
  WALLET_TYPE_APPLE,
  WALLET_TYPE_GOOGLE,
  GOOGLE_PAY_MASTERCARD_WALLET_ID,
  GOOGLE_PAY_VISA_WALLET_ID,
  GOOGLE_PAY_FISERV_WALLET_ID,
  APPLE_PAY_MASTERCARD_WALLET_ID,
  APPLE_PAY_VISA_WALLET_ID,
  APPLE_PAY_FISERV_WALLET_ID,
} from './addToWalletConstants';

// SDK integration constants
const WALLET_COUNTRY_SET = 'US';
const WALLET_LANG_SET = 'en';

export let setAccountForWallet = {
  issuerNonce: '',
  accountId: '',
  cardType: '',
};

export const setInitialPropertiesForSDK = (
  accountId: string,
  cardType: string,
) => {
  const issuerNonce: string = customAlphabet(
    '1234567890abcdefghijklmnopqrstuvwxyz',
    6,
  )().toUpperCase();
  setAccountForWallet = { accountId, cardType, issuerNonce };
};

// callBack is assigning/defined at the time of initialize param, but set values during selection inside SDK
export const selectionCallBack = (response: { payload: string }): void => {
  const { accountId, issuerNonce, cardType } = setAccountForWallet;
  const walletType = store.getState().addToWallet.walletType;
  const productType = store.getState().addToWallet.productType;
  if (response.payload) {
    const data = {
      country: WALLET_COUNTRY_SET,
      issuerNonce,
      accountId,
      passthruFromApp: response.payload,
      cardType,
      walletType,
      productType,
    };
    invokeProvision(data);
  }
};

// responseCallback is OPTIONAL, just to capture the response in console after initializing param
export const responseCallback = (response: { result: string }): void => {
  if (response) {
    console.log('Wallet result :', response.result);
    if (response.result === 'success') {
      console.log('success');
    }
  }
};

/* Step:[1] initialize the param SDK on page load */
/**
 * @description ---> setPolicyParams initializes the FiservSDK
 * @param userDetails ---> first name and last name of user
 * @param buttonId ---> button id - apple or google or samsung
 */
export const setPolicyParams = (userDetails: UserDetails, buttonId = '') => {
  const commonPolicyParam = {
    cardholderFirstName: userDetails['cipher.firstName'],
    cardholderLastName: userDetails['cipher.lastName'],
    cardNickname: userDetails['cipher.firstName'],
    isTestEnvironment: VITE_WALLET_IS_TEST_ENVIRONMENT === 'true',
    localeLanguage: WALLET_LANG_SET,
    localeCountry: WALLET_COUNTRY_SET,
  };
  const applePolicyParam = buttonId
    ? {
        APPLE_buttonId: buttonId,
      }
    : null;
  const googlePolicyParams = {
    GOOGLE_integratorId: GOOGLE_INTEGRATOR_ID || '', // attributes are only required by Google
    GOOGLE_hl: GOOGLE_HL || '', // attributes are only required by Google
  };
  // policyParams
  const policyParams: PolicyParams = {
    ...commonPolicyParam,
    ...applePolicyParam,
    ...googlePolicyParams,
  };
  if (
    window.idiSDK?.initialize(
      policyParams,
      selectionCallBack,
      responseCallback,
    ) === false
  ) {
    console.log('IDI SDK Initialize call has failed!');
  }
};

/* Step:[2] On add to wallet button click invoke selection and callback provison and add the card */
export const onSelectionAddToWallet = (walletType: string) => {
  /* walletType --> we need to pass the walletType(wallet/APPLE_PAY or wallet/GOOGLE_PAY)
   * and need to add walletType to IDI sdk.selection()
   */
  //Invoke the SDK selection
  if (window.idiSDK?.selection(walletType, selectionCallBack) === false) {
    console.log('IDI SDK Selection call has failed!');
  }
};

/**
 * @description ---> invokeProvision is /provision Api call for walletProvision
 * @param invokeProvisionPayload ---> this payload will contain clientInformation
 * @returns ---> passthruToIdiSdk
 */
export const invokeProvision = async (
  invokeProvisionPayload: InvokeProvisionPayload,
): Promise<any> => {
  const {
    country,
    issuerNonce,
    passthruFromApp,
    walletType,
    cardType,
    accountId,
    productType,
  } = invokeProvisionPayload;
  const cardTypeToUppercase: string = cardType?.toLocaleUpperCase() || '';
  const sessionId = sessionStorage.getItem(SESSION_ID_SESSION_STORAGE_KEY);
  const walletId =
    walletType && getWalletID(walletType, cardTypeToUppercase, productType);
  const data = {
    issuerNonce,
    passthruFromApp,
    [CIPHER_ACCOUNT_ID]: accountId, //this should be account number as a guid,
    accountIdType: 'guid',
    requestUri: 'https://www.syf.com/',
    country,
    walletId,
  };
  const authToken = store.getState().session.token;
  const payload: AxiosRequestConfig = {
    method: 'POST',
    url: PROVISION_API_ENDPOINT,
    headers: {
      Authorization: `Bearer ${authToken}`,
      'X-SYF-Request-TrackingId': sessionId,
    },
    data,
  };
  try {
    const response: AxiosResponse = await axios(payload);
    if (response?.data?.passthruToIdiSdk) {
      console.log(
        'Instant Provision success',
        response?.data?.passthruToIdiSdk,
      );
      window.idiSDK?.addCard(response?.data?.passthruToIdiSdk);
    } else {
      console.log('Instant Provision failure');
    }
    return response?.data?.passthruToIdiSdk;
  } catch (error) {
    console.log(error);
  }
};

export const getWalletID = (
  walletType: string,
  cardType: string,
  productType: string,
) => {
  const pattern = /Visa|Mastercard/i;
  const formattedCardType: any = cardType.match(pattern)?.toString();
  const clientId = store.getState().addToWallet.clientId;
  const DC_MASTERCARD = {
    DUAL_CARD: {
      appleWalletId: APPLE_PAY_MASTERCARD_WALLET_ID, //50110030273
      googleWalletId: GOOGLE_PAY_MASTERCARD_WALLET_ID, //50120834693
      cardType: formattedCardType,
    },
  };
  const DC_VISA = {
    DUAL_CARD: {
      appleWalletId: APPLE_PAY_VISA_WALLET_ID, //'40010030273',
      googleWalletId: GOOGLE_PAY_VISA_WALLET_ID, //'40010075001',
      cardType: formattedCardType,
    },
  };
  const PLCC_CARD = {
    PRIVATE_LABEL: {
      appleWalletId: APPLE_PAY_FISERV_WALLET_ID, //'11110030273',
      googleWalletId: GOOGLE_PAY_FISERV_WALLET_ID, //'NA',
      cardType: formattedCardType,
    },
  };
  const FISERV_CARD = {
    DUAL_CARD: {
      appleWalletId: APPLE_PAY_FISERV_WALLET_ID, //'11110030273',
      googleWalletId: GOOGLE_PAY_MASTERCARD_WALLET_ID, //50120834693
      cardType: formattedCardType,
    },
  };

  const walletIds: any = {
    athome: {
      ...DC_MASTERCARD,
      ...PLCC_CARD,
    },
    belk: {
      ...DC_MASTERCARD,
      ...PLCC_CARD,
    },
    jcrew: {
      ...DC_MASTERCARD,
      ...PLCC_CARD,
    },
    virgin: {
      ...DC_MASTERCARD,
    },
    tru: {
      ...FISERV_CARD,
    },
    tjx: {
      ...DC_MASTERCARD,
    },
    aeoutfitters: {
      ...DC_VISA,
      ...PLCC_CARD,
    },
    verizon: {
      ...DC_VISA,
    },
  };
  if (walletType === WALLET_TYPE_APPLE) {
    return walletIds[clientId][productType].appleWalletId;
  } else if (walletType === WALLET_TYPE_GOOGLE) {
    return walletIds[clientId][productType].googleWalletId;
  }
};
